import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { QrCode, Mail, Trophy, Send, Star, ArrowRight, Check, Clock, User } from "lucide-react";
import type { BlogPost } from "@shared/schema";

function Navigation() {
  return (
    <header className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
        <Link href="/" data-testid="link-home">
          <img src="/logo.png" alt="ReviewKit" className="h-10 object-contain" />
        </Link>
        <nav className="hidden md:flex items-center gap-6">
          <Link href="/features" className="text-sm text-muted-foreground hover:text-foreground" data-testid="link-features">
            Features
          </Link>
          <Link href="/pricing" className="text-sm text-muted-foreground hover:text-foreground" data-testid="link-pricing">
            Pricing
          </Link>
          <Link href="/docs" className="text-sm text-muted-foreground hover:text-foreground" data-testid="link-docs">
            Docs
          </Link>
          <Link href="/blog" className="text-sm text-muted-foreground hover:text-foreground" data-testid="link-blog">
            Blog
          </Link>
        </nav>
        <div className="flex items-center gap-3">
          <Link href="/login">
            <Button variant="ghost" size="sm" data-testid="button-login">
              Login
            </Button>
          </Link>
          <Link href="/signup">
            <Button size="sm" data-testid="button-signup">
              Sign Up Free
            </Button>
          </Link>
        </div>
      </div>
    </header>
  );
}

function HeroSection() {
  return (
    <section className="py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-6 text-center">
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6" data-testid="text-hero-headline">
          Get More Google Reviews Without the Enterprise Price Tag
        </h1>
        <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto mb-8" data-testid="text-hero-subheadline">
          The beautiful, simple way for HVAC, plumbing, and service businesses to collect 5-star reviews. No SMS fees. No bloated features. Just results.
        </p>
        <Link href="/signup">
          <Button size="lg" className="gap-2" data-testid="button-hero-cta">
            Start Free
            <ArrowRight className="w-4 h-4" />
          </Button>
        </Link>
        <p className="text-sm text-muted-foreground mt-4">
          Trusted by 500+ service businesses
        </p>
      </div>
    </section>
  );
}

function FeaturesSection() {
  const features = [
    {
      icon: QrCode,
      title: "QR Codes That Convert",
      description: "Generate unique QR codes for your business and each staff member. Print them, stick them in vans, hand them to customers.",
    },
    {
      icon: Mail,
      title: "Beautiful Email Requests",
      description: "Send branded review requests with photos of completed work. Customers remember the job and leave detailed reviews.",
    },
    {
      icon: Trophy,
      title: "Staff Leaderboards",
      description: "See which team members generate the most reviews. Gamify your reputation and reward top performers.",
    },
  ];

  return (
    <section className="py-16 bg-muted/30">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <Card key={index} className="border-0 bg-transparent shadow-none" data-testid={`card-feature-${index}`}>
              <CardContent className="pt-6 text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-md bg-primary/10 text-primary mb-4">
                  <feature.icon className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorksSection() {
  const steps = [
    {
      number: "1",
      title: "Send a Request",
      description: "Email or show a QR code after completing a job",
      icon: Send,
    },
    {
      number: "2",
      title: "Customer Rates You",
      description: "They tap stars on a beautiful mobile page",
      icon: Star,
    },
    {
      number: "3",
      title: "Reviews Flow to Google",
      description: "Happy customers go straight to Google Reviews",
      icon: Check,
    },
  ];

  return (
    <section className="py-16">
      <div className="max-w-7xl mx-auto px-6">
        <h2 className="text-3xl md:text-4xl font-semibold text-center mb-12" data-testid="text-how-it-works-title">
          How It Works
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {steps.map((step, index) => (
            <div key={index} className="text-center" data-testid={`step-${index}`}>
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary text-primary-foreground text-xl font-bold mb-4">
                {step.number}
              </div>
              <h3 className="text-lg font-semibold mb-2">{step.title}</h3>
              <p className="text-muted-foreground">{step.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PricingPreviewSection() {
  return (
    <section className="py-16 bg-muted/30">
      <div className="max-w-7xl mx-auto px-6 text-center">
        <h2 className="text-3xl md:text-4xl font-semibold mb-4" data-testid="text-pricing-preview-title">
          Simple, Honest Pricing
        </h2>
        <p className="text-muted-foreground mb-12">
          Start free. Upgrade when you're ready.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
          <Card className="relative border-2 border-primary overflow-visible" data-testid="card-pricing-lifetime">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
              <Badge variant="default" className="shadow-sm">BEST VALUE</Badge>
            </div>
            <CardContent className="pt-8 pb-6">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 text-primary mb-4">
                <Trophy className="w-6 h-6" />
              </div>
              <div className="text-4xl font-bold mb-1">$79</div>
              <div className="text-sm text-muted-foreground mb-4">one-time payment</div>
              <div className="text-lg font-semibold mb-3">Lifetime Access</div>
              <ul className="text-sm text-muted-foreground space-y-2 text-left">
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-primary shrink-0" />
                  500 requests/month
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-primary shrink-0" />
                  Unlimited staff members
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-primary shrink-0" />
                  All future updates included
                </li>
              </ul>
            </CardContent>
          </Card>
          <Card data-testid="card-pricing-pro">
            <CardContent className="pt-8 pb-6">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-muted text-muted-foreground mb-4">
                <Star className="w-6 h-6" />
              </div>
              <div className="text-4xl font-bold mb-1">$29</div>
              <div className="text-sm text-muted-foreground mb-4">/month</div>
              <div className="text-lg font-semibold mb-3">Pro Plan</div>
              <ul className="text-sm text-muted-foreground space-y-2 text-left">
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-primary shrink-0" />
                  Unlimited requests
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-primary shrink-0" />
                  Everything in Lifetime
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-primary shrink-0" />
                  Priority support
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>
        <Link href="/pricing">
          <Button variant="outline" className="mt-8 gap-2" data-testid="button-view-pricing">
            View Pricing
            <ArrowRight className="w-4 h-4" />
          </Button>
        </Link>
      </div>
    </section>
  );
}

function estimateReadTime(content: string): number {
  const wordsPerMinute = 200;
  const words = content.split(/\s+/).length;
  return Math.max(1, Math.ceil(words / wordsPerMinute));
}

function BlogTeaserSection() {
  const { data: posts, isLoading } = useQuery<BlogPost[]>({
    queryKey: ["/api/blog"],
  });

  const latestPosts = posts?.slice(0, 3) || [];

  if (isLoading) {
    return (
      <section className="py-16 bg-muted/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-semibold mb-4">From Our Blog</h2>
            <p className="text-muted-foreground">Tips and insights for growing your online reputation</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="space-y-4">
                <Skeleton className="h-32 w-full rounded-lg" />
                <Skeleton className="h-4 w-1/4" />
                <Skeleton className="h-6 w-3/4" />
                <Skeleton className="h-4 w-full" />
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (!latestPosts.length) {
    return null;
  }

  return (
    <section className="py-16 bg-muted/30">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-semibold mb-4" data-testid="text-blog-teaser-headline">
            From Our Blog
          </h2>
          <p className="text-muted-foreground">
            Tips and insights for growing your online reputation
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {latestPosts.map((post) => (
            <Link key={post.id} href={`/blog/${post.slug}`} data-testid={`link-blog-teaser-${post.id}`}>
              <Card className="h-full overflow-hidden hover-elevate cursor-pointer">
                {post.heroImageUrl ? (
                  <div 
                    className="h-32 bg-cover bg-center"
                    style={{ backgroundImage: `url(${post.heroImageUrl})` }}
                  />
                ) : (
                  <div className="h-32 bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
                    <span className="text-3xl font-bold text-primary/30">R</span>
                  </div>
                )}
                <CardContent className="p-4">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    {post.tags?.slice(0, 1).map((tag) => (
                      <Badge key={tag} variant="outline" className="text-xs">{tag}</Badge>
                    ))}
                  </div>
                  <h3 className="font-semibold mb-2 line-clamp-2">{post.title}</h3>
                  <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      {post.author}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {estimateReadTime(post.content)} min
                    </span>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
        <div className="text-center mt-8">
          <Link href="/blog">
            <Button variant="outline" className="gap-2" data-testid="button-view-blog">
              View All Articles
              <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}

function CTASection() {
  return (
    <section className="py-16">
      <div className="max-w-7xl mx-auto px-6 text-center">
        <h2 className="text-3xl md:text-4xl font-semibold mb-6" data-testid="text-cta-headline">
          Ready to grow your reputation?
        </h2>
        <Link href="/signup">
          <Button size="lg" className="gap-2" data-testid="button-final-cta">
            Get Started Free
            <ArrowRight className="w-4 h-4" />
          </Button>
        </Link>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t py-12">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <img src="/logo.png" alt="ReviewKit" className="h-10 object-contain" />
          <nav className="flex flex-wrap items-center justify-center gap-6">
            <Link href="/features" className="text-sm text-muted-foreground hover:text-foreground" data-testid="footer-link-features">
              Features
            </Link>
            <Link href="/pricing" className="text-sm text-muted-foreground hover:text-foreground" data-testid="footer-link-pricing">
              Pricing
            </Link>
            <Link href="/docs" className="text-sm text-muted-foreground hover:text-foreground" data-testid="footer-link-docs">
              Docs
            </Link>
            <Link href="/blog" className="text-sm text-muted-foreground hover:text-foreground" data-testid="footer-link-blog">
              Blog
            </Link>
            <Link href="/contact" className="text-sm text-muted-foreground hover:text-foreground" data-testid="footer-link-contact">
              Contact
            </Link>
            <Link href="/privacy" className="text-sm text-muted-foreground hover:text-foreground" data-testid="footer-link-privacy">
              Privacy
            </Link>
            <Link href="/terms" className="text-sm text-muted-foreground hover:text-foreground" data-testid="footer-link-terms">
              Terms
            </Link>
            <Link href="/login" className="text-sm text-muted-foreground hover:text-foreground" data-testid="footer-link-login">
              Login
            </Link>
          </nav>
          <div className="text-sm text-muted-foreground">
            {new Date().getFullYear()} ReviewKit. All rights reserved.
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function Landing() {
  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <main>
        <HeroSection />
        <FeaturesSection />
        <HowItWorksSection />
        <PricingPreviewSection />
        <BlogTeaserSection />
        <CTASection />
      </main>
      <Footer />
    </div>
  );
}
